import { formatDate } from "@angular/common";
import { FORMAT_DATE_LOCALE, ForecastNotAvailableDesc, PollutionForecastTypes } from "../models/app-constants";

export const hexToBase64 = function(hexStr : string) {
	let base64 = "";
    
	try
	{
		base64 =  Buffer.from(hexStr, 'hex').toString('base64');
	}
	catch(ex)
	{
        
	}
	if("" === base64)
	{
		let str = "";
		for(let i = 0; i < hexStr.length; i++) 
		{
			str += !(i - 1 & 1) ? String.fromCharCode(parseInt(hexStr.substring(i - 1, i + 1), 16)) : ""
		}
		base64 = btoa(str);
	}   

	return base64;
    
}


/**         
    * Convert a base64 string in a Blob according to the data and contentType.
    * 
    * @param b64Data {String} Pure base64 string without contentType
    * @param contentType {String} the content type of the file i.e (image/jpeg - image/png - text/plain)
    * @param sliceSize {Int} SliceSize to process the byteCharacters
    * @see http://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
    * @return Blob
**/
export const _b64toBlob = function(b64Data : any, contentType : string, sliceSize : number) {
	contentType = contentType || '';
	sliceSize = sliceSize || 512;

	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);

		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, {type: contentType});
	return blob;
}      


export const getDateOrdinal = function(inDate : Date) {
	const dt = inDate.getDate();

	const st = [1, 21, 31];
	const nd = [2, 22];
	const rd = [3, 23];
    
	if(-1 < st.indexOf(dt))
	{
		return "st";
	}
	else if(-1 < nd.indexOf(dt))
	{
		return "nd";
	}
	else if(-1 < rd.indexOf(dt))
	{
		return "rd";
	}
	else if((dt >= 4 && dt <= 20) ||
        (dt >= 24 && dt <= 30))
	{
		return "th";
	}
	else
	{
		return "";
	}
}

export const getDayName = function(date : Date, short = false, locale = "en-AU")
{
	return date.toLocaleDateString(locale, { weekday : short ? "short" : "long" });        
}

export const getMonthName = function(date : Date, short = false, locale = "en-AU")
{
	return date.toLocaleDateString(locale, { month : short ? "short" : "long" });        
}

export const getForecastDateDescription = function(inDate : Date) {
	if(inDate && "Invalid Date" !== inDate?.toString() && 
		"function" === typeof(inDate.getDate) &&
        "function" === typeof(inDate.toLocaleDateString))
	{
		return `Forecast for ${getDayName(inDate)} ${inDate.getDate()}${getDateOrdinal(inDate)} ${getMonthName(inDate)}`;
	}
    
	return ForecastNotAvailableDesc;    
}

export const getLastUpdatedDescription = (inDate : Date) => {
	if(inDate && "Invalid Date" !== inDate?.toString() && 
		"function" === typeof(inDate.getDate) &&
        "function" === typeof(inDate.toLocaleDateString))
	{
		const timeZoneOffset = new Date().getTimezoneOffset();
		return formatDateAEST(inDate, "ccc, dd/L, haa");
		//return formatDate(inDate, "ccc, dd/L, haa", "en-AU", 'UTC +9:30 / +10:30');
		// const hour = inDate.getHours();
		// const hourStr = (hour % 12) + (hour < 12 ? "am" : "pm");
		// return `${getDayName(inDate, true)}, ${inDate.getDate()}/${inDate.getMonth() + 1} ${hourStr}`;
	}
    
	return "";    
}

export const getTimezoneOffset = () => {
	const timeZoneOffset = new Date().getTimezoneOffset();
	const hours = Math.abs(Math.floor(timeZoneOffset / 60))?.toString().padStart(2, "0");
	const minutes = Math.abs(timeZoneOffset % 60)?.toString().padStart(2, "0");
	const sign = Math.sign(timeZoneOffset) < 0 ? "-" : Math.sign(timeZoneOffset) > 0 ? "+" : "";
	const timeZone = sign + hours + ":" + minutes ;

	return timeZone;
}
export const formatDateAEST = (inDate : Date, outFormat : string, locale : string = FORMAT_DATE_LOCALE) => {
	let outDate = "";
	try
	{
		const timeZone = getTimezoneOffset();
		outDate = formatDate(inDate, outFormat, locale);
		return outDate;
	}
	catch(ex)
	{

	}

	return outDate;	
}


export const dateWithTimeZone = (timeZone : string, year : number, month : number, 
	day : number, hour : number, minute : number, second : number) => {
	const date = new Date(Date.UTC(year, month, day, hour, minute, second));
  
	const utcDate = new Date(date.toLocaleString(FORMAT_DATE_LOCALE, { timeZone: "UTC" }));
	const tzDate = new Date(date.toLocaleString(FORMAT_DATE_LOCALE, { timeZone: timeZone }));
	const offset = utcDate.getTime() - tzDate.getTime();
  
	date.setTime( date.getTime() + offset );
  
	return date;
}


export const convertToAESTDateTime = (dateTime : any) => {
	let dt = dateTime;
	if(dateTime)
	{	
		try
		{
			if("string" === typeof(dateTime))
			{
				dt = new Date(Date.parse(dateTime?.toString()));			
			}
			else if(dateTime != "Invalid Date" && ("toISOString" in dateTime) && 
				"function" === typeof(dateTime.toISOString))
			{
				dt = new Date(Date.parse(dateTime?.toISOString()));			
			}

			// To Do : Account for time zone offset if application is used outside New South Wales
		}
		catch(ex)
		{

		}		
	}	
	
	return dt;
}

export const getPollutionForecastDescText = function(pollutionForecast : string) 
{		
	switch (pollutionForecast) 
	{
		case PollutionForecastTypes.POLLUTION_LIKELY:
		case PollutionForecastTypes.POLLUTION_UNLIKELY:
		case PollutionForecastTypes.POLLUTION_POSSIBLE:
			return (!pollutionForecast?.toString()?.trim()?.toLocaleLowerCase()?.startsWith("pollution") ?
				"Pollution "   : "") + pollutionForecast?.toString() ;			
		default:
			return pollutionForecast?.toString() ?? "";		
	}	
}